import React, { useEffect, useState } from 'react';
import styles from './AssignDoctorControls.module.scss';
import { AxiosError } from 'axios';
import { Icon, Text } from 'components';
import { appActions } from 'store/app/slice';
import { selectCurrentUser } from 'store/auth/selectors';
import { selectPatientPageTimestamp } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { PatientCaseErrorMessages } from 'types';

type Props = {
  assignDoctorID?: string;
  canRemoveAssignment: boolean;
  patientCaseId: number;
};

export const AssignDoctorControls = ({ canRemoveAssignment, assignDoctorID, patientCaseId }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useSelector(selectCurrentUser);
  const timestamp = useSelector(selectPatientPageTimestamp);

  useEffect(() => {
    dispatch(patientsActions.setPatientPageTimestamp());
  }, [dispatch]);

  const onAssignHandler = async () => {
    if (!timestamp || isLoading) return;

    if (!assignDoctorID) {
      try {
        setIsLoading(true);
        await dispatch(
          patientsActions.assignPatientCaseToDoctor({
            assignCaseToDoctorData: {
              checkFrom: timestamp,
            },
            patientCaseId,
          }),
        ).unwrap();

        dispatch(patientsActions.setPatientPageTimestamp());
      } catch (e: any) {
        if (e instanceof AxiosError && e.response?.data.type === PatientCaseErrorMessages.assignedDoctorWasChanged) {
          onOpenReassingModalHandler();
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onRemoveAssignmentWarningHandler = () => {
    dispatch(appActions.setIsModalOpened({ name: 'removeAssignment', isOpened: true }));
  };

  const onOpenReassingModalHandler = () => {
    dispatch(appActions.setIsModalOpened({ name: 'reassignUser', isOpened: true }));
  };

  const renderContent = () => {
    const unassigned = !assignDoctorID;
    const assignedToMe = assignDoctorID === currentUser?.id;
    const assignedToMeCanRemove = assignedToMe && canRemoveAssignment;
    const assignedNotToMe = assignDoctorID && assignDoctorID !== currentUser?.id;

    if (unassigned) {
      return (
        <div className={styles.unAssignDoctorTextWrapper} onClick={onAssignHandler}>
          <Text type="overline" className={styles.unAssignDoctorText}>
            Assign to me
          </Text>
        </div>
      );
    }
    if (assignedToMeCanRemove) {
      return (
        <div className={styles.iconWrapper} onClick={onRemoveAssignmentWarningHandler}>
          <Icon name="Close" className={styles.iconWhite} />
        </div>
      );
    }
    if (assignedNotToMe) {
      return (
        <div className={styles.unAssignDoctorTextWrapper} onClick={onOpenReassingModalHandler}>
          <Text type="overline" className={styles.unAssignDoctorText}>
            Reassign
          </Text>
        </div>
      );
    }
    return null;
  };

  return <div className={styles.unAssignDoctor}>{renderContent()}</div>;
};
